/**
 * Consistency check between Semi-Structured Interview symptom / domain
 * decision counts and the Lead clinician's ADHD Diagnostic Decision.
 *
 * Deliberately arithmetic only (regulatory-safe): it does NOT interpret
 * clinical meaning. It compares the same count thresholds the Diagnostic
 * Decision page already uses for Criterion A / B / D prefills against the
 * recorded Fully met / Partially met / Not met choice.
 *
 * Used by the DD page to open a decision dialog (SLaM / 3 Borough) when the
 * clinician picks an ADHD outcome that does not line up with those counts.
 *
 * Thresholds (DSM-5 adult ADHD, matching 1_DD.js prefills):
 * - Criterion A adulthood: ≥5 Inattention OR ≥5 Hyperactivity-Impulsivity
 * - Criterion B childhood: ≥3 symptoms across IA + H/I
 * - Criterion D: at least one domain of functional impairment in adulthood
 */

/** Positive ADHD diagnostic decisions (Fully / Partially met). */
// 'Yes' / 'No' is the CYP ADHD vocabulary (Oct 2026); the rest is adult.
const POSITIVE_DECISIONS = new Set(['Yes', 'Fully met', 'Partially met']);

/**
 * Count how many items in a list have a truthy value for the given field.
 * @param {Array<{adulthood?: boolean, childhood?: boolean}>} items
 * @param {'adulthood'|'childhood'} field
 * @returns {number}
 */
const countTrue = (items, field) =>
  (items || []).filter((item) => item?.[field] === true).length;

/**
 * Evaluate whether SSI decision counts support the DD decision.
 *
 * Reason strings are written for clinicians (plain language) — they appear
 * in the on-selection dialog on the Diagnostic Decision page.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.diagnosisDecision - DD diagnosisDecision
 *   ('Fully met' | 'Partially met' | 'Not met' | other)
 * @param {Array} params.attentionDeficitData - rows with { adulthood, childhood }
 * @param {Array} params.hyperactivityImpulsivityData - rows with { adulthood, childhood }
 * @param {Array} params.domainImpairmentData - rows with { adulthood, childhood }
 * @returns {{ consistent: boolean, reasons: string[], counts: Object }}
 */
export const evaluateSSIDdConsistency = ({
  diagnosisDecision,
  attentionDeficitData = [],
  hyperactivityImpulsivityData = [],
  domainImpairmentData = [],
} = {}) => {
  // No decision yet → nothing to flag.
  if (!diagnosisDecision) {
    return {
      consistent: true,
      reasons: [],
      counts: null,
    };
  }

  const iaAdulthood = countTrue(attentionDeficitData, 'adulthood');
  const hiAdulthood = countTrue(hyperactivityImpulsivityData, 'adulthood');
  const iaChildhood = countTrue(attentionDeficitData, 'childhood');
  const hiChildhood = countTrue(hyperactivityImpulsivityData, 'childhood');
  const childhoodTotal = iaChildhood + hiChildhood;
  const domainsAdulthood = countTrue(domainImpairmentData, 'adulthood');

  const counts = {
    iaAdulthood,
    hiAdulthood,
    childhoodTotal,
    domainsAdulthood,
  };

  // Criterion A adulthood threshold: ≥5 in either domain.
  const criterionAMet = iaAdulthood >= 5 || hiAdulthood >= 5;
  // Criterion B childhood: ≥3 symptoms across both domains.
  const criterionBMet = childhoodTotal >= 3;
  // Criterion D: at least one adulthood domain impaired.
  const criterionDMet = domainsAdulthood >= 1;

  const thresholdsMet = criterionAMet && criterionBMet && criterionDMet;
  const isPositive = POSITIVE_DECISIONS.has(diagnosisDecision);
  const isNegative = diagnosisDecision === 'Not met' || diagnosisDecision === 'No';

  const reasons = [];

  if (isPositive && !thresholdsMet) {
    // Positive DD decision but SSI counts fall short of the usual thresholds.
    if (!criterionAMet) {
      reasons.push(
        `Fewer than 5 adulthood symptoms are marked present in Inattention ` +
          `(${iaAdulthood} of 5) and in Hyperactivity-Impulsivity ` +
          `(${hiAdulthood} of 5). Usually at least 5 are needed in one of these areas.`
      );
    }
    if (!criterionBMet) {
      reasons.push(
        `Fewer than 3 childhood symptoms are marked present across Inattention ` +
          `and Hyperactivity-Impulsivity (currently ${childhoodTotal} of 3).`
      );
    }
    if (!criterionDMet) {
      reasons.push(
        'No adulthood areas of day-to-day impact are marked as present. ' +
          'Usually at least one is needed.'
      );
    }
  } else if (isNegative && thresholdsMet) {
    // Negative DD decision but SSI counts meet all of the usual thresholds.
    reasons.push(
      `The Semi-Structured Interview marks enough symptoms and day-to-day ` +
        `impact to usually support an ADHD diagnosis (at least 5 adulthood ` +
        `symptoms in one area, at least 3 childhood symptoms in total, and ` +
        `at least one adulthood area of impact), but you selected "${diagnosisDecision}".`
    );
  }

  return {
    consistent: reasons.length === 0,
    reasons,
    counts,
  };
};

/**
 * Whether the current ADHD Diagnostic Decision mismatches the SSI counts AND
 * the clinician has NOT yet resolved that mismatch.
 *
 * This is the single predicate the DD page uses to decide whether to re-arm
 * the SSI-decision-mismatch dialog when the Lead tries to leave / confirm.
 * It deliberately layers "is there a mismatch right now?" (delegated to
 * {@link evaluateSSIDdConsistency}, which reads live SSI counts) with the two
 * ways a mismatch can be considered resolved:
 *
 *  1. A Lead SSI override is already active — the Lead has committed to editing
 *     the SSI (via "Edit the SSI now" or "Finish the Diagnostic Decision
 *     first"), so we must never keep nagging them.
 *  2. The Lead explicitly chose "Keep the SSI decisions" for THIS exact
 *     decision — recorded as `acknowledgedDecision`. It only clears the gate
 *     for the decision that was acknowledged, so flipping to a different
 *     mismatching decision re-arms it.
 *
 * Pure and side-effect free so it can be unit-tested in isolation and reused
 * anywhere the same gate is needed.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.diagnosisDecision - current DD decision
 * @param {Array} params.attentionDeficitData - rows with { adulthood, childhood }
 * @param {Array} params.hyperactivityImpulsivityData - rows with { adulthood, childhood }
 * @param {Array} params.domainImpairmentData - rows with { adulthood, childhood }
 * @param {boolean} params.overrideActive - whether a Lead SSI override is active
 * @param {string|null|undefined} params.acknowledgedDecision - the decision the
 *   Lead acknowledged via "Keep the SSI decisions", if any
 * @returns {boolean}
 */
export const isUnresolvedSSIDdMismatch = ({
  diagnosisDecision,
  attentionDeficitData = [],
  hyperactivityImpulsivityData = [],
  domainImpairmentData = [],
  overrideActive = false,
  acknowledgedDecision = null,
} = {}) => {
  // No decision selected → nothing to enforce.
  if (!diagnosisDecision) return false;
  // Override already active → the Lead has committed to handling the SSI.
  if (overrideActive) return false;

  const { consistent } = evaluateSSIDdConsistency({
    diagnosisDecision,
    attentionDeficitData,
    hyperactivityImpulsivityData,
    domainImpairmentData,
  });
  // No mechanical mismatch → nothing to resolve.
  if (consistent) return false;

  // Mismatch exists: only treated as resolved if the Lead explicitly kept the
  // SSI decisions for THIS exact decision (acknowledgement is decision-specific).
  return acknowledgedDecision !== diagnosisDecision;
};

/**
 * Compute the refreshed Criteria A-D selections from the latest SSI-count-based
 * prefills, used by the DD page while a Lead SSI override is active.
 *
 * When the Lead takes over the SSI and changes symptom / domain counts, the
 * Criteria A-D already persisted on the DD (from the pre-override counts) become
 * stale. Each question in `questionPanels` carries a `prefill` recomputed from
 * the live counts; this helper diffs the current `selections` against those
 * fresh prefills for the SSI-derived criterion keys and returns which cells to
 * update — skipping any criterion the Lead deliberately toggled this session.
 *
 * Pure and side-effect free: the caller applies `next` to state and writes
 * `persist` to Firestore only when `changed` is true. Kept here so the diff
 * logic is unit-testable without rendering the DD page.
 *
 * @param {Object} params
 * @param {Array<{questions: Array<{key?: string, prefill?: any}>}>} params.questionPanels
 * @param {Array<Array<any>>} params.selections - current 2D selections
 * @param {Set<string>|Array<string>} params.criterionKeys - SSI-derived A-D keys
 * @param {Set<string>|Array<string>} [params.manuallyToggledKeys] - keys the Lead
 *   changed this session (never overwritten)
 * @returns {{ next: Array<Array<any>>, persist: Object, changed: boolean }}
 */
export const computeCriterionRefresh = ({
  questionPanels = [],
  selections = [],
  criterionKeys,
  manuallyToggledKeys,
} = {}) => {
  const keySet = criterionKeys instanceof Set ? criterionKeys : new Set(criterionKeys || []);
  const toggledSet =
    manuallyToggledKeys instanceof Set
      ? manuallyToggledKeys
      : new Set(manuallyToggledKeys || []);

  const next = selections.map((panel) => (Array.isArray(panel) ? panel.slice() : panel));
  const persist = {};
  let changed = false;

  questionPanels.forEach((panel, panelIndex) => {
    (panel?.questions || []).forEach((question, questionIndex) => {
      if (!question?.key || !keySet.has(question.key)) return;
      if (toggledSet.has(question.key)) return;
      const fresh = question.prefill ?? null;
      if (fresh === null) return; // no count-based prefill available
      if (next[panelIndex]?.[questionIndex] !== fresh) {
        next[panelIndex][questionIndex] = fresh;
        persist[question.key] = fresh;
        changed = true;
      }
    });
  });

  return { next, persist, changed };
};

/**
 * Interpret the result of a Lead SSI takeover (overrideSSIModule via
 * TriggerOverrideSSI) into a simple proceed/stop decision.
 *
 * Idempotent-safe: if the module has already been taken over (e.g. a race, or
 * the Lead started a takeover earlier from the patient overview), the cloud
 * function returns a failed-precondition. Because edit access is effectively
 * already granted in that case, we treat it as success so the Lead is not
 * blocked. Any other failure is surfaced so the caller can show an error and
 * abort navigation.
 *
 * Kept pure (no side effects) so the caller owns the follow-up refresh /
 * navigation and so this branch logic can be unit-tested in isolation.
 *
 * @param {{ success?: boolean, message?: string, error?: { code?: string } }} result
 * @returns {{ ok: boolean, message?: string }}
 */
export const shouldProceedAfterTakeover = (result) => {
  if (!result) return { ok: false, message: undefined };
  if (result.success) return { ok: true };
  if (
    result.error?.code === 'functions/failed-precondition' &&
    /already been overridden/i.test(result.message || '')
  ) {
    return { ok: true };
  }
  return { ok: false, message: result.message };
};

export default evaluateSSIDdConsistency;
